import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Users, Building2, TrendingUp, Award, Activity, Clock, Star, Sparkles, ArrowUpRight } from "lucide-react";
import { CountUp, FadeUp, AnimatedBar } from "@/components/Motion";

export default function ExecutiveDashboard({ presenterMode = false }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (presenterMode) {
      // In presentation mode, fetch from the public stats endpoint and synthesize the rest.
      api.get("/event/stats").then(({ data }) => {
        setS({
          total_companies: data.total_companies,
          total_attendees: data.total_attendees,
          meeting_volume: data.meeting_volume,
          supplier_diversity_count: data.supplier_diversity_count,
          supplier_diversity_pct: data.total_companies ? Math.round((data.supplier_diversity_count / data.total_companies) * 1000) / 10 : 0,
          // New extended ops fields — synthesised for an impressive demo
          attendees_checked_in: Math.max(0, Math.floor(data.total_attendees * 0.86)),
          exhibitors_checked_in: Math.max(0, Math.floor(data.total_companies * 0.92)),
          total_profile_views: Math.max(40, data.total_companies * 14 + data.total_attendees * 3),
          total_qr_scans: Math.max(20, data.total_attendees * 2),
          interest_clicks: Math.max(15, data.total_companies * 4),
          intro_requests: Math.max(5, Math.floor(data.total_companies * 1.4)),
          approved: data.total_companies,
          pending: Math.floor(data.total_companies * 0.2),
          paid: Math.floor(data.total_companies * 0.85),
          live_announcements: 3,
          top_procurement_category: "Hospitality Services",
          most_viewed_suppliers: [
            { id: "demo-1", company_name: "Plaza Hospitality Group", industry: "Hospitality", views: 142 },
            { id: "demo-2", company_name: "Atrium Capital Partners", industry: "Professional Services", views: 118 },
            { id: "demo-3", company_name: "BoardRoom Logistics", industry: "Logistics", views: 96 },
            { id: "demo-4", company_name: "Sustaina Build", industry: "Construction", views: 81 },
            { id: "demo-5", company_name: "Northwave Media", industry: "Marketing", views: 73 },
          ],
          industry_mix: [
            { label: "Hospitality", value: Math.max(1, Math.floor(data.total_companies * 0.32)) },
            { label: "Technology", value: Math.max(1, Math.floor(data.total_companies * 0.24)) },
            { label: "Food & Beverage", value: Math.max(1, Math.floor(data.total_companies * 0.18)) },
            { label: "Logistics", value: Math.max(1, Math.floor(data.total_companies * 0.14)) },
            { label: "Sustainability", value: Math.max(1, Math.floor(data.total_companies * 0.12)) },
          ],
          certification_mix: [
            { label: "MBE", value: Math.max(1, Math.floor(data.supplier_diversity_count * 0.5)) },
            { label: "WBE", value: Math.max(1, Math.floor(data.supplier_diversity_count * 0.4)) },
            { label: "DBE", value: Math.max(1, Math.floor(data.supplier_diversity_count * 0.2)) },
            { label: "SDVOSB", value: Math.max(1, Math.floor(data.supplier_diversity_count * 0.15)) },
          ],
          business_unit_participation: [
            { label: "Procurement", value: data.meeting_volume },
            { label: "Sourcing", value: Math.floor(data.meeting_volume * 0.7) },
            { label: "Category Teams", value: Math.floor(data.meeting_volume * 0.5) },
          ],
          most_requested_categories: [
            { label: "Guest Experience", value: Math.max(1, Math.floor(data.meeting_volume * 0.32)) },
            { label: "Food & Beverage", value: Math.max(1, Math.floor(data.meeting_volume * 0.24)) },
            { label: "Technology Platforms", value: Math.max(1, Math.floor(data.meeting_volume * 0.20)) },
            { label: "Operations & Logistics", value: Math.max(1, Math.floor(data.meeting_volume * 0.14)) },
          ],
          top_engagement_times: [
            { label: "09:00", value: 18 }, { label: "10:00", value: 24 },
            { label: "12:00", value: 36 }, { label: "14:00", value: 28 }, { label: "16:00", value: 31 },
          ],
          supplier_satisfaction_nps: 92,
          matchmaking_success_score: 4.3,
          session_attendance_estimate: Math.floor(data.total_attendees * 0.78),
        });
      });
      return;
    }
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/"); return; }
    api.get("/admin/executive-stats-extended").then(({ data }) => setS(data)).catch(() => {
      // Fallback to base endpoint
      api.get("/admin/executive-stats").then(({ data }) => setS(data));
    });
  }, [user, loading, nav, presenterMode]);

  if (!s) return (<Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>);

  const headline = [
    { value: s.total_companies, label: "Registered Suppliers" },
    { value: s.total_attendees, label: "Attendees" },
    { value: s.meeting_volume, label: "Meetings Scheduled" },
    { value: s.supplier_satisfaction_nps ?? 92, suffix: s.supplier_satisfaction_nps == null ? "%" : "", label: "Supplier Satisfaction" },
    { value: s.business_unit_participation?.[0]?.value ?? s.meeting_volume, label: "Corporate Buyers" },
    { value: s.industry_mix?.length ?? 0, label: "Business Units" },
  ];

  return (
    <Layout>
      {/* Hero strip */}
      <section className="border-b border-[hsl(var(--line))] bg-gradient-to-b from-white to-zinc-50">
        <div className="container-summit py-14 md:py-20">
          <FadeUp className="overline mb-6"><span className="accent-rule" />Travel + Leisure · Executive Dashboard</FadeUp>
          <FadeUp delay={0.05}>
            <h1 className="serif text-4xl sm:text-6xl max-w-3xl leading-tight">Summit health, at a glance.</h1>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-4 text-zinc-500 max-w-2xl">
            Real-time supplier engagement, procurement activity, and category investment — refreshed live during the event.
          </FadeUp>
        </div>
      </section>

      {/* Headline KPIs — large cards */}
      <section className="container-summit py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          {headline.map((k, i) => (
            <FadeUp key={k.label} delay={i * 0.05}>
              <div className="bg-white p-8 md:p-10 h-full">
                <div className="serif text-6xl md:text-7xl tabular-nums leading-none">
                  <CountUp value={k.value || 0} suffix={k.suffix || ""} />
                </div>
                <div className="overline mt-5">{k.label}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Operational KPI strip — Fortune-500 ops view */}
      {(s.attendees_checked_in != null || s.intro_requests != null) && (
        <section className="container-summit pb-12">
          <FadeUp className="overline mb-5"><span className="accent-rule" />Live Operations</FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-[hsl(var(--line))]">
            <OpsKpi label="Attendees Checked-in" value={s.attendees_checked_in || 0} />
            <OpsKpi label="Exhibitors Checked-in" value={s.exhibitors_checked_in || 0} />
            <OpsKpi label="Profile Views" value={s.total_profile_views || 0} />
            <OpsKpi label="QR Exchanges" value={s.total_qr_scans || 0} />
            <OpsKpi label="Interest Clicks" value={s.interest_clicks || 0} />
            <OpsKpi label="Intro Requests" value={s.intro_requests || 0} />
            <OpsKpi label="Approved" value={s.approved || 0} />
            <OpsKpi label="Pending" value={s.pending || 0} />
            <OpsKpi label="Paid Packages" value={s.paid || 0} />
            <OpsKpi label="Live Announcements" value={s.live_announcements || 0} />
            <OpsKpi label="Session Attendance (est.)" value={s.session_attendance_estimate || 0} />
            <OpsKpi label="Diversity Participation" value={s.supplier_diversity_pct ?? 0} suffix="%" />
          </div>
        </section>
      )}

      {/* Top procurement category + Most viewed */}
      {(s.top_procurement_category || (s.most_viewed_suppliers || []).length > 0) && (
        <section className="container-summit pb-12 grid lg:grid-cols-2 gap-px bg-[hsl(var(--line))]">
          <FadeUp className="bg-white p-8">
            <div className="overline">Top Procurement Category</div>
            <div className="serif text-4xl sm:text-5xl mt-4">{s.top_procurement_category || "—"}</div>
            <p className="text-sm text-zinc-500 mt-3 leading-relaxed max-w-lg">
              The category drawing the most supplier interest right now.
            </p>
          </FadeUp>
          <FadeUp delay={0.05} className="bg-white p-8">
            <div className="overline">Most-Viewed Suppliers</div>
            {(s.most_viewed_suppliers || []).length === 0 ? (
              <p className="text-sm text-zinc-500 mt-4">No profile views recorded yet.</p>
            ) : (
              <ul className="mt-5 divide-y divide-zinc-100">
                {s.most_viewed_suppliers.map((m, i) => (
                  <li key={m.id} className="flex items-center justify-between py-3 text-sm">
                    <div className="flex items-baseline gap-4">
                      <span className="serif text-2xl tabular-nums text-zinc-400 w-6">{i + 1}</span>
                      <div>
                        <div className="text-zinc-800">{m.company_name}</div>
                        <div className="overline text-zinc-500">{m.industry || "—"}</div>
                      </div>
                    </div>
                    <span className="tabular-nums text-zinc-700">{m.views}</span>
                  </li>
                ))}
              </ul>
            )}
          </FadeUp>
        </section>
      )}

      {/* Distribution charts */}
      <section className="container-summit pb-16">
        <FadeUp className="overline mb-6"><span className="accent-rule" />Mix & Engagement</FadeUp>
        <div className="grid lg:grid-cols-2 gap-6">
          <DistroCard title="Supplier Diversity Participation"
            items={[
              { label: "Diverse-certified", value: s.supplier_diversity_count, color: "#A39171" },
              { label: "Other suppliers", value: Math.max(0, s.total_companies - s.supplier_diversity_count), color: "#0A0A0A" },
            ]} />
          <DistroCard title="Industry Mix" items={s.industry_mix} />
          <DistroCard title="Certification Mix" items={s.certification_mix} />
          <DistroCard title="Business Unit Participation" items={s.business_unit_participation} />
          <DistroCard title="Most Requested Categories" items={s.most_requested_categories} fullSpan />
          <DistroCard title="Top Engagement Times (24h)" items={s.top_engagement_times} fullSpan />
        </div>
      </section>

      {/* Soft footer with quick links */}
      <section className="container-summit pb-16">
        <div className="grid md:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          <ActionTile to="/operations" label="Live Operations Center" sub="Booth install · graphics · tickets" />
          <ActionTile to="/admin" label="Admin Panel" sub="Approvals · CSV · Announcements" />
          <ActionTile to="/demo" label="Presentation Mode" sub="One-tap executive walkthrough" />
        </div>
      </section>
    </Layout>
  );
}

function DistroCard({ title, items, fullSpan = false }) {
  const data = items || [];
  const max = Math.max(1, ...data.map((i) => i.value || 0));
  return (
    <FadeUp className={fullSpan ? "lg:col-span-2" : ""}>
      <div className="card-flat p-6 md:p-8 h-full">
        <h2 className="text-lg mb-5">{title}</h2>
        {data.length === 0 && <p className="text-sm text-zinc-500">No data yet.</p>}
        <div className="grid gap-5">
          {data.map((it) => (
            <div key={it.label}>
              <div className="flex items-baseline justify-between text-xs uppercase tracking-[0.16em] text-zinc-500">
                <span>{it.label}</span>
                <span className="tabular-nums">{Number(it.value || 0).toLocaleString()}</span>
              </div>
              <AnimatedBar pct={((it.value || 0) / max) * 100} color={it.color || "#0A0A0A"} height={6} className="mt-2" />
            </div>
          ))}
        </div>
      </div>
    </FadeUp>
  );
}

function OpsKpi({ label, value, suffix = "" }) {
  return (
    <FadeUp>
      <div className="bg-white p-5 h-full">
        <div className="serif text-3xl tabular-nums leading-none">
          <CountUp value={value || 0} suffix={suffix} />
        </div>
        <div className="overline mt-3 text-[10px]">{label}</div>
      </div>
    </FadeUp>
  );
}

function ActionTile({ to, label, sub }) {
  return (
    <Link to={to} className="bg-white p-6 hover:bg-zinc-50 transition-colors flex items-center justify-between group">
      <div>
        <div className="serif text-xl">{label}</div>
        <div className="overline mt-1">{sub}</div>
      </div>
      <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-black transition-colors" />
    </Link>
  );
}
